/** CT6: build a new tree from the closed vocabulary, never send the editor's innerHTML. */
export interface MessageBody { text: string; html?: string }
const tags: Record<string, string> = { B: 'b', STRONG: 'b', I: 'i', EM: 'i', U: 'u', S: 's', STRIKE: 's', DEL: 's', UL: 'ul', OL: 'ol', LI: 'li', P: 'p', DIV: 'p', BR: 'br', A: 'a' };
const discarded = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'TEMPLATE']);
export function safeHref(value: string): string | null {
  const href = value.trim();
  if (/[\u0000-\u0020\u007f]/.test(href)) return null;
  try { return ['http:', 'https:', 'mailto:'].includes(new URL(href).protocol) ? href : null; } catch { return null; }
}
export function cleanNodes(source: Node): DocumentFragment {
  const result = document.createDocumentFragment();
  for (const child of source.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      const lines = (child.textContent ?? '').replace(/\u200b/g, '').split(/\r?\n/);
      lines.forEach((line, i) => { if (i) result.append(document.createElement('br')); result.append(document.createTextNode(line)); });
    } else if (child instanceof Element && !discarded.has(child.tagName.toUpperCase())) {
      const children = cleanNodes(child);
      const tag = tags[child.tagName.toUpperCase()];
      if (!tag) { result.append(children); continue; }
      const href = tag === 'a' ? safeHref(child.getAttribute('href') ?? '') : null;
      if (tag === 'a' && !href) { result.append(children); continue; }
      const next = document.createElement(tag);
      if (href) next.setAttribute('href', href);
      next.append(children);
      result.append(next);
    }
  }
  return result;
}
export function plainText(root: Node): string {
  let text = '';
  for (const node of root.childNodes) {
    if (node.nodeType === Node.TEXT_NODE) text += node.textContent ?? '';
    else if (node instanceof Element) {
      if (node.tagName === 'BR') text += '\n';
      else {
        const block = /^(P|DIV|LI|UL|OL)$/.test(node.tagName);
        if (block && text && !text.endsWith('\n')) text += '\n';
        text += plainText(node);
        if (block && !text.endsWith('\n')) text += '\n';
      }
    }
  }
  return text.replace(/\u200b/g, '');
}
export function editorMessage(root: Node): MessageBody {
  const safe = document.createElement('div');
  safe.append(cleanNodes(root));
  const text = plainText(safe).trim();
  // Paragraphs and line breaks alone are plain text, just as Shift+Enter in the textarea is.
  return safe.querySelector('b,i,u,s,ul,ol,a') ? { text, html: safe.innerHTML } : { text };
}
export function selectRange(range: Range) {
  const selection = window.getSelection();
  selection?.removeAllRanges(); selection?.addRange(range);
}
export function insertNodes(range: Range, nodes: Node) {
  range.deleteContents();
  const last = nodes instanceof DocumentFragment ? nodes.lastChild : nodes;
  range.insertNode(nodes);
  if (last) range.setStartAfter(last);
  range.collapse(true); selectRange(range);
}

/** Formatting edits only the remembered editor selection, even after focus moves to a toolbar. */
export function formatRange(root: HTMLElement, range: Range, tag: 'b' | 'i' | 'u' | 's' | 'ul' | 'ol' | 'a', href?: string) {
  if (tag === 'a' && (!href || !safeHref(href) || range.collapsed)) return;
  const parent = range.commonAncestorContainer instanceof Element ? range.commonAncestorContainer : range.commonAncestorContainer.parentElement;
  const current = parent?.closest(tag);
  if (current && current !== root && root.contains(current) && tag !== 'a') {
    const start = current.firstChild, end = current.lastChild;
    current.replaceWith(...current.childNodes);
    if (start && end) { range.setStartBefore(start); range.setEndAfter(end); }
    selectRange(range); return;
  }
  if (!range.collapsed && tag !== 'a') {
    const fragment = range.cloneContents();
    const holder = document.createElement('div'); holder.append(fragment);
    const walker = document.createTreeWalker(holder, NodeFilter.SHOW_TEXT);
    const texts: Text[] = [];
    while (walker.nextNode()) if (walker.currentNode.textContent?.trim()) texts.push(walker.currentNode as Text);
    if (texts.length && texts.every(node => node.parentElement?.closest(tag))) {
      for (const marked of holder.querySelectorAll(tag)) marked.replaceWith(...marked.childNodes);
      const plain = document.createDocumentFragment(); plain.append(...holder.childNodes);
      const first = plain.firstChild, last = plain.lastChild;
      range.deleteContents(); range.insertNode(plain);
      if (first && last) { range.setStartBefore(first); range.setEndAfter(last); }
      selectRange(range); return;
    }
  }
  const wrapper = document.createElement(tag);
  if (href) wrapper.setAttribute('href', href);
  const contents = range.extractContents();
  if (tag === 'ul' || tag === 'ol') {
    // A selection of several lines becomes several list items, not a single item containing a list.
    const lines = plainText(contents).replace(/\n$/, '').split('\n');
    for (const line of lines) { const item = document.createElement('li'); item.textContent = line || '\u200b'; wrapper.append(item); }
  } else wrapper.append(contents.hasChildNodes() ? contents : document.createTextNode('\u200b'));
  range.insertNode(wrapper);
  range.selectNodeContents(wrapper);
  if (wrapper.textContent === '\u200b') range.collapse(false);
  selectRange(range);
}
